/* ═══════════════════════════════════════════════════════════════
   FilterBar — ultra scoping for Forecasting's Plan mode. Reuses
   DataTable's filter primitives verbatim (FilterMenu / AnchoredPopover /
   ColumnFilters / filterSummaryText / isFilterActive) instead of
   re-rolling a bespoke filter popover — Person / Department / Team /
   Project are modeled as virtual filter "columns" so the exact UI
   DataTable ships (search-within-columns, multiselect, active-filter
   chips with one-click clear) is inherited for free.

   Scope (Mine / My team / My projects / All), pivot, layout, horizon,
   and search stay page-level presets — not column filters, since
   they change WHICH rows/columns exist rather than narrowing a set.
   ═══════════════════════════════════════════════════════════════ */

import React, { useMemo, useRef, useState } from 'react';
import { Filter, Copy, Search, X } from 'lucide-react';
import type { TeamMember, Team, Department, Project, DashboardView } from '../../../shared/types';
import {
  FilterMenu,
  type Column,
  type ColumnFilters,
  isFilterActive,
  filterSummaryText,
} from '../../components/DataTable';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { SavedViewsMenu } from './SavedViewsMenu';
import type { HorizonWeeks, Pivot, PersonLayout, ProjectBreakdown, Scope } from './types';

export function FilterBar({
  members,
  teams,
  departments,
  projects,
  filters,
  onFiltersChange,
  scope,
  onScopeChange,
  scopeOptions,
  searchQuery,
  onSearchChange,
  pivot,
  onPivotChange,
  personLayout,
  onPersonLayoutChange,
  projectBreakdown,
  onProjectBreakdownChange,
  horizonWeeks,
  onHorizonWeeksChange,
  canBulk,
  onCopyPrevWeek,
  copying,
  savedViews,
  activeViewId,
  viewIsDirty,
  onApplyView,
  onSaveView,
  onUpdateView,
  onDeleteView,
  onSetDefaultView,
  onRenameView,
  onShareToggle,
  currentUserId,
  canShareViews,
}: {
  members: TeamMember[];
  teams: Team[];
  departments: Department[];
  projects: Project[];
  filters: ColumnFilters;
  onFiltersChange: (next: ColumnFilters) => void;
  scope: Scope;
  onScopeChange: (s: Scope) => void;
  scopeOptions: { value: Scope; label: string; available: boolean }[];
  searchQuery: string;
  onSearchChange: (v: string) => void;
  pivot: Pivot;
  onPivotChange: (p: Pivot) => void;
  personLayout: PersonLayout;
  onPersonLayoutChange: (v: PersonLayout) => void;
  projectBreakdown: ProjectBreakdown;
  onProjectBreakdownChange: (v: ProjectBreakdown) => void;
  horizonWeeks: HorizonWeeks;
  onHorizonWeeksChange: (n: HorizonWeeks) => void;
  canBulk: boolean;
  onCopyPrevWeek: () => Promise<void>;
  copying: boolean;
  savedViews: DashboardView[];
  activeViewId: string | null;
  viewIsDirty: boolean;
  onApplyView: (v: DashboardView) => void;
  onSaveView: (name: string) => Promise<void>;
  onUpdateView: () => Promise<void>;
  onDeleteView: (id: string) => Promise<void>;
  onSetDefaultView: (id: string) => Promise<void>;
  onRenameView: (id: string, name: string) => Promise<void>;
  onShareToggle: (id: string) => Promise<void>;
  currentUserId: string;
  canShareViews?: boolean;
}) {
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const filterBtnRef = useRef<HTMLButtonElement>(null);

  const columns: Column<unknown>[] = useMemo(
    () => [
      { key: 'person', label: 'Person' },
      { key: 'department', label: 'Department' },
      { key: 'team', label: 'Team' },
      { key: 'project', label: 'Project' },
    ],
    []
  );
  const columnMeta = useMemo(
    () => ({
      person: {
        filterType: 'multiselect' as const,
        options: members
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((m) => ({ value: m.id, label: m.name })),
      },
      department: {
        filterType: 'multiselect' as const,
        options: departments.map((d) => ({ value: d.id, label: d.name })),
      },
      team: {
        filterType: 'multiselect' as const,
        options: teams.map((t) => ({ value: t.id, label: t.name })),
      },
      project: {
        filterType: 'multiselect' as const,
        options: projects.map((p) => ({
          value: p.id,
          label: p.projectNumber ? `${p.projectNumber} ${p.name}` : p.name,
        })),
      },
    }),
    [members, departments, teams, projects]
  );

  const activeFilterKeys = Object.keys(filters).filter((k) => isFilterActive(filters[k]));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        borderBottom: '1px solid var(--bd)',
        background: 'var(--bg)',
        flexShrink: 0,
      }}
    >
      <div
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-2)' }}
      >
        {/* Pivot: person-first vs project-first — same grid, transposed. */}
        <Tabs value={pivot} onValueChange={(v) => onPivotChange(v as Pivot)}>
          <TabsList className="border-b-0 gap-1 rounded-md bg-surface-alt p-0.5">
            <TabsTrigger value="person" className="rounded px-3 py-1 text-xs -mb-0 border-b-0">
              People
            </TabsTrigger>
            <TabsTrigger value="project" className="rounded px-3 py-1 text-xs -mb-0 border-b-0">
              Projects
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {pivot === 'person' ? (
          <Tabs value={personLayout} onValueChange={(v) => onPersonLayoutChange(v as PersonLayout)}>
            <TabsList className="border-b-0 gap-1 rounded-md bg-surface-alt p-0.5">
              <TabsTrigger value="department" className="rounded px-3 py-1 text-xs border-b-0">
                Dept
              </TabsTrigger>
              <TabsTrigger value="team" className="rounded px-3 py-1 text-xs border-b-0">
                Team
              </TabsTrigger>
              <TabsTrigger value="flat" className="rounded px-3 py-1 text-xs border-b-0">
                Flat
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : (
          <Tabs
            value={projectBreakdown}
            onValueChange={(v) => onProjectBreakdownChange(v as ProjectBreakdown)}
          >
            <TabsList className="border-b-0 gap-1 rounded-md bg-surface-alt p-0.5">
              <TabsTrigger value="individual" className="rounded px-3 py-1 text-xs border-b-0">
                Person
              </TabsTrigger>
              <TabsTrigger value="team" className="rounded px-3 py-1 text-xs border-b-0">
                Team
              </TabsTrigger>
              <TabsTrigger value="department" className="rounded px-3 py-1 text-xs border-b-0">
                Dept
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(6),
            minWidth: rem(200),
            flex: '1 1 auto',
          }}
        >
          <Icon icon={Search} em={1.0769} color="var(--tm)" aria-hidden="true" />
          <Input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onSearchChange('');
            }}
            placeholder="Search projects, people, teams…"
            aria-label="Search forecasting"
            style={{ height: rem(30), fontSize: 'calc(var(--font-size-base) * 12 / 13)' }}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }} />

        {canBulk && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCopyConfirmOpen(true)}
            disabled={copying}
          >
            <Icon icon={Copy} em={1.2} />
            {copying ? 'Copying…' : 'Copy prev week'}
          </Button>
        )}

        {/* Horizon */}
        <Tabs
          value={String(horizonWeeks)}
          onValueChange={(v) => onHorizonWeeksChange(Number(v) as HorizonWeeks)}
        >
          <TabsList className="border-b-0 gap-1 rounded-md bg-surface-alt p-0.5">
            <TabsTrigger value="6" className="rounded px-2 py-1 text-xs border-b-0">
              6wk
            </TabsTrigger>
            <TabsTrigger value="8" className="rounded px-2 py-1 text-xs border-b-0">
              8wk
            </TabsTrigger>
            <TabsTrigger value="12" className="rounded px-2 py-1 text-xs border-b-0">
              12wk
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Ultra filter — reused wholesale from DataTable */}
        <div style={{ position: 'relative' }}>
          <Button
            ref={filterBtnRef}
            variant={activeFilterKeys.length > 0 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterMenuOpen((o) => !o)}
          >
            <Icon icon={Filter} em={1.2} />
            Filter
            {activeFilterKeys.length > 0 && <span>({activeFilterKeys.length})</span>}
          </Button>
          {filterMenuOpen && filterBtnRef.current && (
            <FilterMenu
              columns={columns}
              columnMeta={columnMeta}
              filters={filters}
              onChange={onFiltersChange}
              onClose={() => setFilterMenuOpen(false)}
              anchorRect={filterBtnRef.current.getBoundingClientRect()}
              ignoreRef={filterBtnRef}
            />
          )}
        </div>

        <SavedViewsMenu
          savedViews={savedViews}
          activeViewId={activeViewId}
          currentUserId={currentUserId}
          viewIsDirty={viewIsDirty}
          onApplyView={onApplyView}
          onSaveAs={() => setSaveViewOpen(true)}
          onUpdateActive={onUpdateView}
          onDeleteView={onDeleteView}
          onSetDefaultView={onSetDefaultView}
          onRenameView={onRenameView}
          onShareToggle={onShareToggle}
          canShareViews={canShareViews}
        />
      </div>

      {/* Scope presets — separate row so it reads as "who am I looking at",
          distinct from the column filter chips below ("narrowed by…"). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: rem(6) }}>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            color: 'var(--tm)',
            textTransform: 'uppercase',
            letterSpacing: '.05em',
          }}
        >
          Scope
        </span>
        <div
          role="radiogroup"
          aria-label="People scope"
          style={{ display: 'flex', gap: 'var(--space-1)' }}
        >
          {scopeOptions.map((opt) => (
            <button
              key={opt.value}
              role="radio"
              aria-checked={scope === opt.value}
              disabled={!opt.available}
              onClick={() => onScopeChange(opt.value)}
              style={{
                padding: `var(--space-1) ${rem(10)}`,
                borderRadius: 'var(--radius-full)',
                border: `1px solid ${scope === opt.value ? 'var(--ac)' : 'var(--bd)'}`,
                background:
                  scope === opt.value
                    ? 'color-mix(in srgb, var(--ac) 14%, transparent)'
                    : 'var(--sa)',
                color: !opt.available
                  ? 'var(--tm)'
                  : scope === opt.value
                    ? 'var(--ac)'
                    : 'var(--tx)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: scope === opt.value ? 700 : 500,
                cursor: opt.available ? 'pointer' : 'not-allowed',
                opacity: opt.available ? 1 : 0.5,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active-filter chips */}
      {activeFilterKeys.length > 0 && (
        <div style={{ display: 'flex', gap: rem(6), flexWrap: 'wrap' }}>
          {activeFilterKeys.map((key) => {
            const col = columns.find((c) => c.key === key);
            if (!col) return null;
            const fv = filters[key];
            return (
              <span
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-1)',
                  fontSize: 'var(--font-size-sm)',
                  padding: `${rem(3)} ${rem(6)} ${rem(3)} ${rem(10)}`,
                  borderRadius: 'var(--radius-full)',
                  background: 'color-mix(in srgb, var(--ac) 10%, transparent)',
                  color: 'var(--tx)',
                  border: '1px solid color-mix(in srgb, var(--ac) 25%, transparent)',
                }}
              >
                <strong style={{ fontWeight: 600 }}>{col.label}:</strong>
                {filterSummaryText(col, fv)}
                <button
                  onClick={() => {
                    const next = { ...filters };
                    delete next[key];
                    onFiltersChange(next);
                  }}
                  aria-label={`Clear ${col.label} filter`}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--tm)',
                    display: 'flex',
                    padding: rem(2),
                  }}
                >
                  <Icon icon={X} size="md" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={copyConfirmOpen}
        onOpenChange={setCopyConfirmOpen}
        title="Copy previous week?"
        description="Every manual forecast from the prior week is copied into the current week. Existing cells in the destination week are overwritten. This can't be undone automatically."
        confirmLabel="Copy"
        destructive={false}
        busy={copying}
        onConfirm={async () => {
          await onCopyPrevWeek();
          setCopyConfirmOpen(false);
        }}
      />

      <ConfirmDialog
        open={saveViewOpen}
        onOpenChange={setSaveViewOpen}
        title="Save current view"
        description={
          <div style={{ marginTop: 'var(--space-1)' }}>
            <Input
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              placeholder="View name (e.g. My Team This Quarter)"
              autoFocus
            />
          </div>
        }
        confirmLabel="Save view"
        destructive={false}
        onConfirm={async () => {
          if (!newViewName.trim()) return;
          await onSaveView(newViewName.trim());
          setNewViewName('');
          setSaveViewOpen(false);
        }}
      />
    </div>
  );
}
